/**
 * Gate S8 Step 6: Refactored AnimalDetailDrawer
 *
 * Uses shared AnimalSummaryHeader, AnimalDetailsSection, DrawerChecklistProgress.
 * Behavior stats block replaces the former interactions preview.
 * LinkedTasksList is collapsed by default behind a toggle header.
 * Context prop wired to scroll-to behavior (behavior/interactions → behavior section,
 * checklist → checklist section, others → top of drawer).
 */

import { useState, useRef, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { LinkedTasksList } from "@/components/tasks/linked-tasks-list";
import { AnimalSummaryHeader } from "@/components/animals/AnimalSummaryHeader";
import { AnimalDetailsSection } from "@/components/animals/AnimalDetailsSection";
import { DrawerChecklistProgress } from "@/components/animals/DrawerChecklistProgress";
import { apiRequest } from "@/lib/queryClient";
import {
  Sheet,
  SheetContent,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Circle,
  AlertTriangle,
  ExternalLink,
  FileText,
  ClipboardList,
  ChevronDown,
  ChevronUp,
  PawPrint,
  Clock,
  Award,
} from "lucide-react";
import type { WorkflowDashboardItem } from "@/lib/api/workflow-types";
import { differenceInDays } from "date-fns";
import {
  FieldChange,
  getTimelineChangeLabel,
  deduplicateStatusChanges,
  buildStatusSummary,
  LOCATION_LABELS,
} from "@/lib/timeline-utils";
import { getStayColor } from "@/lib/stay-colors";
import { EvalStatusBadge } from "@/components/animals/EvalStatusBadge";
import { formatReportDate } from "@/lib/utils";
import { formatDateTimeUTC } from "@/lib/date-utils";
import { useIsMobile } from "@/hooks/use-mobile";

interface AnimalDetailDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  animalId: string;
  item?: WorkflowDashboardItem;
  context?: 'behavior' | 'reports' | 'animals' | 'interactions' | 'checklist';
}

interface TimelineSnapshot {
  reportDate: string;
  location: string;
  zoneCode: string | null;
  kennelStatusNorm: string | null;
  substatusNorm: string | null;
  statusCode: string | null;
  adoptionLevelNorm: string | null;
  intakeDate: string | null;
  animalEvalStatus: string | null;
  animalEvalRaw: string | null;
  changesFromPrevious: FieldChange[];
}

interface Stay {
  stayIndex: number;
  admissionGroupId: number;
  startDate: string;
  outcomeDate?: string | null;
  outcomedInReports: boolean;
  snapshots: TimelineSnapshot[];
}

interface Timeline {
  stays: Stay[];
}

interface AnimalData {
  id: string;
  animalId: string;
  name: string;
  species: string;
  photoUrl: string | null;
  intakeDate?: string | null;
  timeline?: Timeline;
  currentStatus?: {
    location: string;
    zoneCode?: string;
    kennelStatusNorm?: "AVAILABLE" | "UNAVAIL" | "STRAY_WAIT" | "EVALUATION" | string;
    substatusNorm?: "MEDICAL" | "BEHAVIOR" | "BEH+MED" | "PENDADOPT" | string;
    kennelStatusSince?: string | null;
    kennelStatusSinceIsApprox?: boolean;
    adoptionLevelNorm?: string;
    lastSeenDate?: string;
  };
  evaluation?: {
    status: string | null;
    label: string | null;
    daysInStatus?: number | null;
  };
  flagSummary?: {
    hasLongNoteFlag: boolean;
    hasKeywordFlag: boolean;
    hasHighlightFlag: boolean;
    flaggedInteractionCount: number;
    lastFlaggedAt: string | null;
  } | null;
  recentObservation?: {
    occurredAt: string;
    scoreCode: string | null;
    scoreBand: string | null;
  } | null;
  scoreBandDistribution?: {
    green: number;
    yellow: number;
    red: number;
  } | null;
}

/**
 * S8.1-6: Internal helper — renders an ExternalLink icon-button that navigates
 * to a profile tab. On desktop: wrapped in a Tooltip. On mobile: bare link only,
 * because Radix Tooltip opens on pointerenter (first tap) and absorbs the click,
 * requiring two taps to navigate. useIsMobile matches the established codebase
 * convention (see report-freshness-modal.tsx, location-strip.tsx).
 */
function DrawerNavLink({
  href,
  label,
  testId,
  onClose,
}: {
  href: string;
  label: string;
  testId: string;
  onClose: () => void;
}) {
  const isMobile = useIsMobile();
  const link = (
    <a href={href} onClick={onClose} data-testid={testId}>
      <Button size="icon" variant="ghost" className="h-6 w-6">
        <ExternalLink className="h-3.5 w-3.5" />
      </Button>
    </a>
  );
  if (isMobile) return link;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{link}</TooltipTrigger>
      <TooltipContent side="left">{label}</TooltipContent>
    </Tooltip>
  );
}

/**
 * S8.1-3 + S8.1-5: Ordinal suffix helper for admission group display.
 * e.g. ordinal(1) → "1st", ordinal(2) → "2nd", ordinal(3) → "3rd", ordinal(11) → "11th"
 */
function ordinal(n: number): string {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] ?? s[v] ?? s[0]);
}

export function AnimalDetailDrawer({
  open,
  onOpenChange,
  animalId,
  item,
  context,
}: AnimalDetailDrawerProps) {
  const [tasksExpanded, setTasksExpanded] = useState(false);
  const [stayExpanded, setStayExpanded] = useState(false);
  const behaviorSectionRef = useRef<HTMLDivElement>(null);
  const checklistSectionRef = useRef<HTMLDivElement>(null);

  // Context prop scroll-to behavior.
  // 150 ms delay lets the Sheet open animation complete before scrollIntoView fires.
  // scrollIntoView walks up the DOM to the Radix ScrollArea viewport (overflow:auto)
  // and scrolls within the drawer panel, not the outer window.
  useEffect(() => {
    if (!open) return;
    const ref =
      context === 'behavior' || context === 'interactions' ? behaviorSectionRef :
      context === 'checklist' ? checklistSectionRef :
      null;
    if (!ref?.current) return;
    const timer = setTimeout(() => {
      ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 150);
    return () => clearTimeout(timer);
  }, [open, context]);

  // Animal data — array key joins to /api/animals/${animalId} via default queryFn
  const { data: animalData, isLoading: animalLoading } = useQuery<AnimalData>({
    queryKey: ["/api/animals", animalId],
    enabled: open && !!animalId,
  });

  // Linked tasks count — shares cache key with LinkedTasksList so zero extra requests
  // when LinkedTasksList is expanded (and the cache is already populated).
  const { data: tasksData } = useQuery<{ tasks: unknown[]; totalCount: number }>({
    queryKey: ["/api/tasks/by-animal", animalId],
    enabled: open && !!animalId,
  });
  const tasksCount = tasksData?.totalCount ?? 0;

  const guidance = item?.guidance;
  const timeline = animalData?.timeline;

  // S8.1-3 + S8.1-5: Direct snapshot access; replaces getCurrentStayEntries / TimelineEntry
  const currentStay = (timeline?.stays?.length
    ? timeline.stays[timeline.stays.length - 1]
    : null) as (Stay | null);
  const reversedSnapshots = currentStay?.snapshots
    ? [...currentStay.snapshots].reverse()
    : [];
  const visibleSnapshots = stayExpanded
    ? reversedSnapshots
    : reversedSnapshots.slice(0, 5);

  // Behavior stats derived values
  const flagSummary = animalData?.flagSummary ?? null;
  const scoreBandDist = animalData?.scoreBandDistribution ?? null;
  const recentObs = animalData?.recentObservation ?? null;
  const recentObsDaysSince = recentObs
    ? Math.floor((Date.now() - new Date(recentObs.occurredAt).getTime()) / 86_400_000)
    : null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        className="!w-full max-w-full sm:!w-[520px] sm:max-w-[520px] p-0 flex flex-col"
        data-testid="animal-detail-drawer"
      >
        {/* Sticky header */}
        <AnimalSummaryHeader
          animalId={animalId}
          name={item?.name ?? animalData?.name ?? null}
          species={item?.species ?? animalData?.species ?? null}
          photoUrl={animalData?.photoUrl ?? null}
          isLoading={animalLoading}
          onClose={() => onOpenChange(false)}
          showFollowButton={true}
          drawerIcon={PawPrint}
          drawerLabel="Animal preview"
        />

        {/* Scrollable content */}
        <ScrollArea className="flex-1">
          <div className="px-6 pb-6 space-y-6">

            {/* Animal details — replaces inline metadata block */}
            <AnimalDetailsSection
              animalId={animalId}
              animalData={animalData ? {
                name: animalData.name,
                species: animalData.species,
                photoUrl: animalData.photoUrl,
                intakeDate: animalData.intakeDate ?? null,
                currentStatus: animalData.currentStatus,
                evaluation: animalData.evaluation,
                track: item?.track ?? null,
              } : null}
              isLoading={animalLoading}
            />

            {/* File count */}
            <AnimalDrawerFileCount animalId={animalId} open={open} onOpenChange={onOpenChange} />

            <Separator />

            {/* Behavior stats block — replaces former Recent Interactions section */}
            <div
              data-testid="animal-detail-drawer-section-behavior"
              ref={behaviorSectionRef}
            >
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-semibold text-foreground/70 uppercase tracking-wider flex items-center gap-1.5">
                  <Award className="w-3.5 h-3.5" /> Behavior
                </p>
                <DrawerNavLink
                  href={`/animals/${encodeURIComponent(animalId)}?tab=behavior`}
                  label="View in profile"
                  testId="link-drawer-behavior-tab"
                  onClose={() => onOpenChange(false)}
                />
              </div>

              {animalLoading ? (
                <div className="space-y-2">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-4 w-1/2" />
                </div>
              ) : (
                <div className="space-y-3 text-sm">
                  {/* Active flags */}
                  <div className="space-y-1">
                    {!flagSummary || (!flagSummary.hasLongNoteFlag && !flagSummary.hasKeywordFlag && !flagSummary.hasHighlightFlag) ? (
                      <p className="text-xs text-muted-foreground">No active flags</p>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {flagSummary.hasLongNoteFlag && (
                          <span className="inline-flex items-center gap-1 text-xs bg-destructive/10 text-destructive rounded px-1.5 py-0.5" data-testid="badge-flag-long-note">
                            <AlertTriangle className="h-3 w-3" />
                            Long note
                          </span>
                        )}
                        {flagSummary.hasKeywordFlag && (
                          <span className="inline-flex items-center gap-1 text-xs bg-destructive/10 text-destructive rounded px-1.5 py-0.5" data-testid="badge-flag-keyword">
                            <AlertTriangle className="h-3 w-3" />
                            Keyword flag
                          </span>
                        )}
                        {flagSummary.hasHighlightFlag && (
                          <span className="inline-flex items-center gap-1 text-xs bg-destructive/10 text-destructive rounded px-1.5 py-0.5" data-testid="badge-flag-highlight">
                            <AlertTriangle className="h-3 w-3" />
                            Highlighted
                          </span>
                        )}
                      </div>
                    )}
                    {flagSummary && flagSummary.flaggedInteractionCount > 0 && (
                      <p className="text-xs text-muted-foreground" data-testid="text-flagged-interaction-count">
                        {flagSummary.flaggedInteractionCount} flagged interaction{flagSummary.flaggedInteractionCount !== 1 ? "s" : ""}
                      </p>
                    )}
                  </div>

                  {/* Score distribution — last 7 days */}
                  {!scoreBandDist || (scoreBandDist.green === 0 && scoreBandDist.yellow === 0 && scoreBandDist.red === 0) ? (
                    <p className="text-xs text-muted-foreground">No interactions in the last 7 days</p>
                  ) : (
                    <div className="flex items-center gap-3 text-xs" data-testid="div-behavior-score-distribution">
                      <span className="text-muted-foreground">Last 7 days:</span>
                      <span className="text-green-600 dark:text-green-400">{scoreBandDist.green} green</span>
                      <span className="text-yellow-600 dark:text-yellow-400">{scoreBandDist.yellow} yellow</span>
                      <span className="text-destructive">{scoreBandDist.red} red</span>
                    </div>
                  )}

                  {/* Most recent observation */}
                  {recentObs ? (
                    <p className="text-xs text-muted-foreground" data-testid="text-behavior-last-observation">
                      Last observation: {formatDateTimeUTC(recentObs.occurredAt)} · Score: {recentObs.scoreCode ?? "N/A"} · {recentObsDaysSince} day{recentObsDaysSince !== 1 ? "s" : ""} ago
                    </p>
                  ) : (
                    <p className="text-xs text-muted-foreground">No observations recorded</p>
                  )}
                </div>
              )}
            </div>

            {context === 'behavior' && (
              <>
                <Separator />

                {/* What the Flowchart Says to Do */}
                <div data-testid="animal-detail-drawer-section-guidance">
                  <h3 className="font-semibold mb-3">What the Flowchart Says to Do</h3>
                  {guidance ? (
                    <div className="space-y-3">
                      <p className="text-sm font-medium">{guidance.guidanceTitle}</p>
                      {guidance.guidanceChecklist.length > 0 ? (
                        <ul className="space-y-2 text-sm">
                          {guidance.guidanceChecklist.map((step, idx) => (
                            <li key={idx} className="flex items-start gap-2">
                              <Circle className="h-4 w-4 mt-0.5 text-muted-foreground flex-shrink-0" />
                              <span>{step}</span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-sm text-muted-foreground italic">No checklist items</p>
                      )}
                      {guidance.confirmSteps.length > 0 && (
                        <div className="mt-3 space-y-2">
                          {guidance.confirmSteps.map((step) => (
                            <div key={step.id} className="text-sm bg-warning/10 border border-warning/20 rounded p-3">
                              <p className="font-medium flex items-center gap-2 text-foreground">
                                <AlertTriangle className="h-4 w-4 text-warning" />
                                {step.question}
                              </p>
                              <div className="mt-2 ml-6 space-y-1 text-foreground/80">
                                <p>If YES: {step.yesGuidance.join(', ')}</p>
                                <p>If NO: {step.noGuidance.join(', ')}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground italic">No guidance available</p>
                  )}
                </div>

                <Separator />

                {/* Why This Guidance */}
                <div data-testid="animal-detail-drawer-section-why">
                  <h3 className="font-semibold mb-2">Why This Guidance</h3>
                  {guidance ? (
                    <div className="space-y-2 text-sm">
                      <p className="text-muted-foreground">Rule ID: {guidance.matchedRuleId}</p>
                      <p>{guidance.rationale}</p>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground italic">No rationale available</p>
                  )}
                </div>
              </>
            )}

            <Separator />

            {/* Checklist progress */}
            <div ref={checklistSectionRef}>
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-semibold text-foreground/70 uppercase tracking-wider flex items-center gap-1.5">
                  <PawPrint className="w-3.5 h-3.5" /> Checklist
                </p>
                <DrawerNavLink
                  href={`/animals/${encodeURIComponent(animalId)}?tab=profile`}
                  label="View in profile"
                  testId="link-drawer-checklist-tab"
                  onClose={() => onOpenChange(false)}
                />
              </div>
              <DrawerChecklistProgress animalId={animalId} open={open} />
            </div>

            <Separator />

            {/* Current Stay — S8.1-2: moved below Checklist; S8.1-3 + S8.1-5: overhauled */}
            <div
              data-testid="animal-detail-drawer-section-status-changes"
              aria-label="Current Stay"
            >
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-foreground/70 uppercase tracking-wider flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5" /> Current Stay
                </p>
                <DrawerNavLink
                  href={`/animals/${encodeURIComponent(animalId || '')}?tab=stays`}
                  label="View in profile"
                  testId="link-drawer-stays-tab"
                  onClose={() => onOpenChange(false)}
                />
              </div>
              {animalLoading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="border-l-2 border-primary/30 pl-3 space-y-1">
                      <div className="flex items-center gap-2">
                        <Skeleton className="h-3 w-20" />
                        <Skeleton className="h-4 w-24 rounded-full" />
                      </div>
                      <Skeleton className="h-3 w-32" />
                    </div>
                  ))}
                </div>
              ) : reversedSnapshots.length > 0 && currentStay ? (
                <>
                  {/* Stay-level header: colored badge + LOS + optional re-admission label */}
                  {(() => {
                    const losEnd = currentStay.outcomeDate
                      ? new Date(currentStay.outcomeDate + "T00:00:00")
                      : new Date();
                    const losDays = differenceInDays(losEnd, new Date(currentStay.startDate));
                    const isOngoing = !currentStay.outcomeDate && !currentStay.outcomedInReports;
                    const losLabel = `${losDays} day${losDays !== 1 ? "s" : ""}${isOngoing ? " (ongoing)" : ""}`;
                    const isReAdmission = currentStay.admissionGroupId > 1;
                    const stayColor = getStayColor(currentStay.stayIndex);
                    return (
                      <div className="flex items-center gap-2 mb-3">
                        <Badge
                          variant="outline"
                          className="text-xs font-semibold shrink-0"
                          style={{
                            borderColor: stayColor,
                            color: stayColor,
                            backgroundColor: `color-mix(in srgb, ${stayColor} 10%, transparent)`,
                          }}
                          data-testid="badge-current-stay-number"
                        >
                          Stay #{currentStay.stayIndex}
                        </Badge>
                        <span className="text-xs text-muted-foreground" data-testid="text-current-stay-los">
                          {isReAdmission
                            ? `${ordinal(currentStay.admissionGroupId)} admission · ${losLabel}`
                            : losLabel}
                        </span>
                      </div>
                    );
                  })()}

                  {/* Snapshot list */}
                  <ul
                    className="space-y-3 text-sm"
                    data-testid="animal-drawer-recent-status-changes"
                  >
                    {visibleSnapshots.map((snapshot, idx) => {
                      const statusSummary = buildStatusSummary(snapshot);
                      const displayChanges = deduplicateStatusChanges(snapshot.changesFromPrevious ?? []);
                      const previousSnapshot = idx < visibleSnapshots.length - 1 ? visibleSnapshots[idx + 1] : null;
                      const currentEvalStatus = snapshot.animalEvalStatus;
                      const previousEvalStatus = previousSnapshot?.animalEvalStatus || null;
                      const hasEvaluationChange = currentEvalStatus !== null && currentEvalStatus !== previousEvalStatus;
                      const locationLabel = LOCATION_LABELS[snapshot.location] ?? snapshot.location;
                      return (
                        <li
                          key={`${snapshot.reportDate}-${idx}`}
                          className="border-l-2 border-primary/30 pl-3"
                          data-testid="animal-drawer-timeline-entry"
                        >
                          {/* Row 1: date + location */}
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-xs">
                              {formatReportDate(snapshot.reportDate, "MMM d, yyyy")}
                            </span>
                            <Badge variant="outline" className="text-xs py-0">
                              {locationLabel}
                            </Badge>
                          </div>
                          {/* Row 2: status summary */}
                          {statusSummary && (
                            <p className="text-xs text-muted-foreground mb-1">
                              {statusSummary}
                            </p>
                          )}
                          {/* Row 3: eval change badge (only when eval status changed)
                              S8.1-B1 fix: add "Behavior evaluation:" label prefix and use
                              className (not style) for getEvaluationColor — matches Stays tab. */}
                          {hasEvaluationChange && currentEvalStatus && (
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xs text-muted-foreground">Behavior evaluation:</span>
                              <EvalStatusBadge
                                status={currentEvalStatus}
                                className="text-xs"
                                data-testid="badge-eval-change"
                              />
                            </div>
                          )}
                          {/* Row 4: field change diffs */}
                          {displayChanges.length > 0 ? (
                            <ul className="space-y-1 text-xs text-muted-foreground">
                              {displayChanges.map((change, changeIdx) => {
                                const label = getTimelineChangeLabel(change.field);
                                return (
                                  <li
                                    key={changeIdx}
                                    className="flex flex-wrap items-center gap-x-1"
                                    data-testid="animal-drawer-recent-status-change-row"
                                  >
                                    <span>{label}:</span>
                                    <span className="line-through">
                                      {change.oldValue || 'None'}
                                    </span>
                                    <span>→</span>
                                    <span className="text-foreground">
                                      {change.newValue || 'None'}
                                    </span>
                                  </li>
                                );
                              })}
                            </ul>
                          ) : (
                            <span className="text-xs text-muted-foreground italic">
                              No changes from previous report
                            </span>
                          )}
                        </li>
                      );
                    })}
                  </ul>

                  {/* Expand/collapse — only when more than 5 snapshots exist */}
                  {reversedSnapshots.length > 5 && (
                    <button
                      type="button"
                      onClick={() => setStayExpanded(v => !v)}
                      className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors mt-2"
                      data-testid="button-toggle-current-stay"
                    >
                      {stayExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                      {stayExpanded ? "Show less" : `Show ${reversedSnapshots.length - 5} more`}
                    </button>
                  )}
                </>
              ) : (
                <p className="text-sm text-muted-foreground">No timeline data available</p>
              )}
            </div>

            <Separator />

            {/* Linked tasks — S8.1-4: static header, lightweight toggle button below */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-foreground/70 uppercase tracking-wider flex items-center gap-1.5">
                  <ClipboardList className="w-3.5 h-3.5" /> Seen in Tasks &amp; Meetings
                </span>
                {tasksCount > 0 && (
                  <Badge variant="secondary" className="text-xs" data-testid="badge-linked-tasks-drawer-count">
                    {tasksCount}
                  </Badge>
                )}
              </div>
              {tasksExpanded && (
                <LinkedTasksList entityType="animal" entityId={animalId} compact hideHeader />
              )}
              <button
                type="button"
                onClick={() => setTasksExpanded(v => !v)}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                data-testid="button-toggle-linked-tasks"
              >
                {tasksExpanded ? (
                  <ChevronUp className="h-3 w-3" />
                ) : (
                  <ChevronDown className="h-3 w-3" />
                )}
                {tasksExpanded
                  ? "Hide"
                  : tasksCount > 0
                    ? `Show ${tasksCount} task${tasksCount !== 1 ? "s" : ""}`
                    : "Show tasks"}
              </button>
            </div>

          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}

function AnimalDrawerFileCount({
  animalId,
  open,
  onOpenChange,
}: {
  animalId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [, setLocation] = useLocation();
  const { data, isLoading } = useQuery<{ count: number }>({
    queryKey: ["/api/animals", animalId, "files", "count"],
    queryFn: async () => apiRequest("GET", `/api/animals/${animalId}/files/count`),
    enabled: open && !!animalId,
  });

  if (isLoading) return <Skeleton className="h-4 w-28" />;
  if (!data || data.count === 0) return null;

  return (
    <div className="flex items-center gap-2 mt-2" data-testid="drawer-file-count">
      <FileText className="h-4 w-4 text-muted-foreground" />
      <span
        className="text-sm text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
        onClick={() => {
          onOpenChange(false);
          setLocation(`/animals/${animalId}?tab=files`);
        }}
        role="link"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onOpenChange(false);
            setLocation(`/animals/${animalId}?tab=files`);
          }
        }}
        data-testid="link-drawer-file-count"
      >
        {data.count} file{data.count !== 1 ? "s" : ""} attached
      </span>
    </div>
  );
}

export default AnimalDetailDrawer;
